import { ADJUST_REASONS, stockRow } from '@khata/core';
import { DateInput, Field, MoneyInput, QtyInput, Select, TextInput } from '../components/index.js';
import { fromApi, isErr, money, quantity, useFields, useSave } from '../lib/form.js';
import { qty as fmtQty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { master, state, submit, today } from '../lib/khata.js';
import { adjustReason } from '../lib/labels.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function AdjustSheet(props: { itemId: string; bin?: 'stock' | 'defective' }) {
  const item = master.value.items.get(props.itemId);
  const row = item ? stockRow(state.value, item) : null;
  const [f, set] = useFields({
    bin: props.bin ?? 'stock',
    dir: 'add',
    qty: '',
    unitCost: row ? String(row.avgCost / 100) : '',
    reason: 'count',
    date: today.value,
    note: '',
  });
  const [error, busy, save] = useSave();
  if (!item || !row) return <Sheet title={t('item_adjust')}>{t('entry_notFound')}</Sheet>;
  const adding = f.dir === 'add';

  const onSave = () =>
    save(async () => {
      const q = quantity(f.qty, 'qty');
      if (isErr(q)) return q;
      let unitCost: number | null = null;
      if (adding) {
        const c = money(f.unitCost, 'unitCost');
        if (isErr(c)) return { message: t('adjust_unitCostRequired'), field: 'unitCost' };
        unitCost = c;
      }
      const r = await submit('stock_adjust', {
        date: f.date,
        itemId: item.id,
        bin: f.bin,
        qty: adding ? q : -q,
        unitCost,
        reason: f.reason,
        note: f.note,
      });
      if (!r.ok) return fromApi(r.error);
      return t('adjust_saved', { name: item.name });
    });

  return (
    <Sheet
      title={t('sheet_adjust', { name: item.name })}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <div class="help">
        {t('adjust_now', { qty: fmtQty(row.qty), unit: item.unit, cost: taka(row.avgCost) })}
        {row.defQty > 0 ? ` · ${fmtQty(row.defQty)} ${t('adjust_inBin')}` : ''}
      </div>
      <div class="grid-3">
        <Field label={t('adjust_where')}>
          <Select value={f.bin} onChange={(v) => set('bin', v)}>
            <option value="stock">{t('stock')}</option>
            <option value="defective">{t('defectiveBin')}</option>
          </Select>
        </Field>
        <Field label={t('adjust_direction')}>
          <Select value={f.dir} onChange={(v) => set('dir', v)}>
            <option value="add">{t('adjust_add')}</option>
            <option value="remove">{t('adjust_remove')}</option>
          </Select>
        </Field>
        <Field label={t('quantity')}>
          <QtyInput
            value={f.qty}
            onInput={(v) => set('qty', v)}
            invalid={error?.field === 'qty'}
            autoFocus
          />
        </Field>
      </div>
      <div class="grid-2">
        {adding ? (
          <Field label={t('adjust_unitCost')}>
            <MoneyInput
              value={f.unitCost}
              onInput={(v) => set('unitCost', v)}
              invalid={error?.field === 'unitCost'}
            />
          </Field>
        ) : (
          <Field label={t('adjust_goesOutAt')}>
            <div class="input" style={{ display: 'flex', alignItems: 'center' }}>
              {t('adjust_avgCost')}
            </div>
          </Field>
        )}
        <Field label={t('adjust_reason')}>
          <Select value={f.reason} onChange={(v) => set('reason', v)}>
            {ADJUST_REASONS.map((r) => (
              <option key={r} value={r}>
                {adjustReason(r)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('note')}>
          <TextInput value={f.note} onInput={(v) => set('note', v)} placeholder={t('optional')} />
        </Field>
      </div>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
